import React, { useState, useEffect, useMemo, useCallback } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  InputLabel,
  Pagination,
  Collapse,
} from '@mui/material';
import { Add as AddIcon, Star as StarIcon, CalendarToday as CalendarIcon, Comment as CommentIcon, Delete as DeleteIcon, Input as InputIcon, Replay as ReplayIcon, DesignServices as DesignServicesIcon, LocalOffer as LocalOfferIcon, AttachMoney as AttachMoneyIcon, AssignmentTurnedIn as AssignmentTurnedInIcon, Sync as SyncIcon, Clear as ClearIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, FilterList as FilterListIcon, PictureAsPdf as PictureAsPdfIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB } from 'date-fns/locale';
import { apiGet, apiPost } from '../../utils/api';
import Tooltip from '@mui/material/Tooltip';

enum ClientStageType {
  INTRARE = 'intrare',
  REVENIRI = 't1', // TODO
  PLANURI = 'planuri',// TODO
  PREOFERTARE = 'preofertare',
  OFERTARE = 'ofertare',
  CONTRACTARE = 'contractare',
}

interface ClientStage {
  id: number;
  stageType: ClientStageType;
  date: string;
  column1: string;
  column2: string;
  createdAt: string;
  updatedAt: string;
}

interface Client {
  id: number;
  name: string;
  jobId: string;
  userId: number;
  createdAt: string;
  updatedAt: string;
  stages: ClientStage[];
  url?: string;
}

interface HierarchyUser {
  id: number;
  username: string;
  level: number;
  managerId: number | null;
  createdAt: string;
}

const STELUTA_OPTIONS = [
  { value: 'red', label: <StarIcon sx={{ color: '#ff0000' }} />, tooltip: 'Urgent' },
  { value: 'orange', label: <StarIcon sx={{ color: '#ff8c00' }} />, tooltip: 'Important' },
  { value: 'blue', label: <StarIcon sx={{ color: '#0066cc' }} />, tooltip: 'Peste 2 luni' },
  { value: 'white', label: <StarIcon sx={{ color: '#ffffff', stroke: '#000000', strokeWidth: 1 }} />, tooltip: 'Lipsa Informatii' },
];

const CALIFICARE_OPTIONS = [
  { value: 'calificat', label: 'Calificat' },
  { value: 'anulat', label: 'Anulat' },
];

const DA_NU_OPTIONS = [
  { value: 'DA', label: 'DA' },
  { value: 'NU', label: 'NU' },
];

const STELUTA_FILTER_OPTIONS = [
  { value: 'normal', label: 'Normal' },
  ...STELUTA_OPTIONS,
];

const STAGE_FILTER_OPTIONS = [
  { value: '', label: 'Toate etapele' },
  { value: 'intrare', label: 'Intrare' },
  { value: 't1', label: 'T1' },
  { value: 'planuri', label: 'Planuri' },
  { value: 'preofertare', label: 'Preofertare' },
  { value: 'ofertare', label: 'Ofertare' },
  { value: 'contractare', label: 'Contractare' },
];

const ClientPipelinePage: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [hyperlink, setHyperlink] = useState('');
  const [extractedData, setExtractedData] = useState<{ customerName: string; url: string; idLucrare: string } | null>(null);
  const [stelutaValues, setStelutaValues] = useState<{ [key: string]: string }>({});
  const [calificareValues, setCalificareValues] = useState<{ [key: string]: string }>({});
  const [dateValues, setDateValues] = useState<{ [key: string]: Date | null }>({});
  const [stelutaTextValues, setStelutaTextValues] = useState<{ [key: string]: string }>({});
  const [semnareValues, setSemnareValues] = useState<{ [key: string]: string }>({});
  const [incasareValues, setIncasareValues] = useState<{ [key: string]: string }>({});
  const [calificareNumberValues, setCalificareNumberValues] = useState<{ [key: string]: number }>({});
  const [manualName, setManualName] = useState('');
  const [manualUrl, setManualUrl] = useState('');
  const [manualError, setManualError] = useState('');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [favoritesStatus, setFavoritesStatus] = useState<{ [clientId: number]: boolean }>({});
  const [selectedStelutaFilter, setSelectedStelutaFilter] = useState<string[]>(['normal']);
  const [showAnulat, setShowAnulat] = useState(false);
  const [showContracted, setShowContracted] = useState(false);
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [commentClientId, setCommentClientId] = useState<number | null>(null);
  const [comments, setComments] = useState<any[]>([]);
  const [newComment, setNewComment] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [hoveredClientId, setHoveredClientId] = useState<number | null>(null);
  const [hoverComments, setHoverComments] = useState<any[]>([]);
  const [hoverLoading, setHoverLoading] = useState(false);
  const [hierarchyUsers, setHierarchyUsers] = useState<HierarchyUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [nameFilter, setNameFilter] = useState('');
  const [debouncedNameFilter, setDebouncedNameFilter] = useState('');
  const [lastStageFilter, setLastStageFilter] = useState<string>('');
  const [intrareStartDate, setIntrareStartDate] = useState<Date | null>(null);
  const [intrareEndDate, setIntrareEndDate] = useState<Date | null>(null);
  const [lastStageStartDate, setLastStageStartDate] = useState<Date | null>(null);
  const [lastStageEndDate, setLastStageEndDate] = useState<Date | null>(null);
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Move populateClientStages definition above fetchClients
  const populateClientStages = (client: Client) => {
    if (!client.stages || client.stages.length === 0) return;

    const stageMap: { [key: string]: ClientStage } = {};
    client.stages.forEach(stage => {
      stageMap[stage.stageType] = stage;
    });

    // Populate Intrare stage (column 0)
    const intrareStage = stageMap['intrare'];
    if (intrareStage) {
      // Date
      if (intrareStage.date) {
        const dateKey = `${client.id}-date-0`;
        setDateValues(prev => ({
          ...prev,
          [dateKey]: new Date(intrareStage.date)
        }));
      }
      // Sursa (column1)
      if (intrareStage.column1) {
        const textKey = `${client.id}-text-0`;
        setStelutaTextValues(prev => ({
          ...prev,
          [textKey]: intrareStage.column1
        }));
      }
      // Calificare number (column2)
      if (intrareStage.column2) {
        const numKey = `${client.id}-calificare-number`;
        setCalificareNumberValues(prev => ({
          ...prev,
          [numKey]: parseFloat(intrareStage.column2) || 0
        }));
      }
    }
    // Populate other stages (columns 1-5)
    const stageTypes = ['t1', 'planuri', 'preofertare', 'ofertare', 'contractare'];
    stageTypes.forEach((stageType, index) => {
      const stage = stageMap[stageType];
      const columnIndex = index + 1;
      if (stage) {
        // Date
        if (stage.date) {
          const dateKey = `${client.id}-date-${columnIndex}`;
          setDateValues(prev => ({
            ...prev,
            [dateKey]: new Date(stage.date)
          }));
        }
        // STELUTA (column1)
        if (stage.column1) {
          const stelutaKey = `${client.id}-${columnIndex}`;
          setStelutaValues(prev => ({
            ...prev,
            [stelutaKey]: stage.column1
          }));
        }
        // Calificare (column2)
        if (stage.column2) {
          const calificareKey = `${client.id}-${columnIndex}`;
          setCalificareValues(prev => ({
            ...prev,
            [calificareKey]: stage.column2
          }));
        }
      }
    });
    // Handle contractare stage special case (Semnare and Incasare)
    const contractareStage = stageMap['contractare'];
    if (contractareStage) {
      // Semnare (column1)
      if (contractareStage.column1) {
        const semnareKey = `${client.id}-semnare`;
        setSemnareValues(prev => ({
          ...prev,
          [semnareKey]: contractareStage.column1
        }));
      }
      // Incasare (column2)
      if (contractareStage.column2) {
        const incasareKey = `${client.id}-incasare`;
        setIncasareValues(prev => ({
          ...prev,
          [incasareKey]: contractareStage.column2
        }));
      }
    }
  };

  // Move fetchClients definition here, before useEffect
  const fetchClients = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch ALL clients (no pagination) for client-side filtering
      let url = '/client?limit=10000'; // Large limit to get all clients
      if (selectedUserId) {
        url = `/client/user/${selectedUserId}?limit=10000`;
      }
      if (favoritesOnly) {
        url += (url.includes('?') ? '&' : '') + 'favorites=true';
      }
      
      // Add name filter to URL
      if (debouncedNameFilter.trim()) {
        url += (url.includes('?') ? '&' : '?') + `name=${encodeURIComponent(debouncedNameFilter.trim())}`;
      }
      
      // Add last stage filter to URL
      if (lastStageFilter.trim()) {
        url += (url.includes('?') ? '&' : '?') + `lastStageType=${encodeURIComponent(lastStageFilter.trim())}`;
      }
      
      // Add Intrare stage date range filters to URL
      if (intrareStartDate) {
        const year = intrareStartDate.getFullYear();
        const month = String(intrareStartDate.getMonth() + 1).padStart(2, '0');
        const day = String(intrareStartDate.getDate()).padStart(2, '0');
        url += (url.includes('?') ? '&' : '?') + `intrareStartDate=${year}-${month}-${day}`;
      }
      if (intrareEndDate) {
        const year = intrareEndDate.getFullYear();
        const month = String(intrareEndDate.getMonth() + 1).padStart(2, '0');
        const day = String(intrareEndDate.getDate()).padStart(2, '0');
        url += (url.includes('?') ? '&' : '?') + `intrareEndDate=${year}-${month}-${day}`;
      }
      
      // Add last completed stage date range filters to URL
      if (lastStageStartDate) {
        const year = lastStageStartDate.getFullYear();
        const month = String(lastStageStartDate.getMonth() + 1).padStart(2, '0');
        const day = String(lastStageStartDate.getDate()).padStart(2, '0');
        url += (url.includes('?') ? '&' : '?') + `lastStageStartDate=${year}-${month}-${day}`;
      }
      if (lastStageEndDate) {
        const year = lastStageEndDate.getFullYear();
        const month = String(lastStageEndDate.getMonth() + 1).padStart(2, '0');
        const day = String(lastStageEndDate.getDate()).padStart(2, '0');
        url += (url.includes('?') ? '&' : '?') + `lastStageEndDate=${year}-${month}-${day}`;
      }
      
      const data = await apiGet(url);
      setClients(data.clients);
      setTotal(data.clients.length); // Total is now the actual client count
      // Populate form fields with existing stage data
      data.clients.forEach((client: Client) => {
        populateClientStages(client);
      });
      // Fetch favorites status for all clients
      if (data.clients.length > 0) {
        const clientIds = data.clients.map((client: Client) => client.id);
        const statusData = await apiGet(`/client/favorites/status?clientIds=${clientIds.join(',')}`);
        setFavoritesStatus(statusData);
      }
    } catch (err: any) {
      setError(err.message);
      setClients([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  // PDF Export function - same approach as Report page
  const exportToPDF = async () => {
    const pipelineElement = document.getElementById('client-pipeline-container');
    if (!pipelineElement) return;

    try {
      // Show loading state
      const exportButton = document.getElementById('export-pdf-btn');
      if (exportButton) {
        exportButton.textContent = 'Se exportă...';
        exportButton.setAttribute('disabled', 'true');
      }

      // Wait a bit for content to render properly
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Replace form elements with their current values as text
      const replacements: Array<{original: any, textElement?: any, wasHidden?: boolean}> = [];
      const formElements = pipelineElement.querySelectorAll('input, select, button, .MuiFormControl-root, .MuiButton-root, .MuiIconButton-root');
      
      formElements.forEach((element: any) => {
        const parent = element.parentNode;
        if (!parent) return;
        
        // Get the current value based on element type
        let displayValue = '';
        if (element.tagName === 'INPUT') {
          displayValue = element.value || element.placeholder || '';
        } else if (element.tagName === 'SELECT') {
          const selectedOption = element.options[element.selectedIndex];
          displayValue = selectedOption ? selectedOption.text : '';
        } else if (element.tagName === 'BUTTON' || element.classList.contains('MuiButton-root')) {
          displayValue = element.textContent || element.innerText || '';
        } else if (element.classList.contains('MuiFormControl-root')) {
          // For Material-UI form controls, try to get the selected value
          const select = element.querySelector('select');
          const input = element.querySelector('input');
          if (select) {
            const selectedOption = select.options[select.selectedIndex];
            displayValue = selectedOption ? selectedOption.text : '';
          } else if (input) {
            displayValue = input.value || input.placeholder || '';
          }
        }
        
        // Only show the element if it has a meaningful value
        if (displayValue && displayValue.trim() !== '' && displayValue !== 'N/A') {
          // Create text element with current value
          const textElement = document.createElement('span');
          textElement.style.cssText = `
            display: inline-block;
            padding: 4px 8px;
            border: 1px solid #ccc;
            background-color: #f5f5f5;
            border-radius: 4px;
            font-size: 12px;
            color: #333;
            margin: 2px;
          `;
          
          // Check if this is a star value and preserve the color
          if (['red', 'orange', 'blue', 'white'].includes(displayValue.toLowerCase())) {
            const starColor = displayValue.toLowerCase() === 'red' ? '#ff0000' :
                             displayValue.toLowerCase() === 'orange' ? '#ff8c00' :
                             displayValue.toLowerCase() === 'blue' ? '#0066cc' :
                             displayValue.toLowerCase() === 'white' ? '#ffffff' : '#333';
            
            textElement.innerHTML = `<span style="color: ${starColor}; font-size: 14px;">★</span>`;
            if (displayValue.toLowerCase() === 'white') {
              textElement.innerHTML = `<span style="color: ${starColor}; font-size: 14px; text-shadow: 1px 1px 1px #000000;">★</span>`;
            }
          } else {
            textElement.textContent = displayValue;
          }
          
          // Replace the original element
          parent.replaceChild(textElement, element);
          replacements.push({original: element, textElement: textElement});
        } else {
          // Hide the element temporarily
          element.style.display = 'none';
          replacements.push({original: element, wasHidden: true});
        }
      });

      const canvas = await html2canvas(pipelineElement, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: pipelineElement.scrollWidth,
        height: pipelineElement.scrollHeight,
      });

      // Restore original elements
      replacements.forEach((replacement) => {
        if (replacement.textElement) {
          // Element was replaced with text, restore it
          const parent = replacement.textElement.parentNode;
          if (parent) {
            parent.replaceChild(replacement.original, replacement.textElement);
          }
        } else if (replacement.wasHidden) {
          // Element was just hidden, restore its display
          replacement.original.style.display = '';
        }
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('l', 'mm', 'a4'); // landscape orientation
      
      const imgWidth = 295; // A4 width in mm (landscape)
      const pageHeight = 210; // A4 height in mm (landscape)
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      // Add title
      pdf.setFontSize(20);
      pdf.text('Client Pipeline', 105, 15, { align: 'center' });
      pdf.setFontSize(12);
      pdf.text(`Generat la: ${new Date().toLocaleDateString('ro-RO')} ${new Date().toLocaleTimeString('ro-RO')}`, 105, 25, { align: 'center' });

      // Add the image
      pdf.addImage(imgData, 'PNG', 0, 35, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add new pages if needed
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Generate filename
      const filename = `client-pipeline-${new Date().toISOString().split('T')[0]}.pdf`;
      
      pdf.save(filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Eroare la generarea PDF-ului. Încercați din nou.');
    } finally {
      // Reset button state
      const exportButton = document.getElementById('export-pdf-btn');
      if (exportButton) {
        exportButton.textContent = 'Export PDF';
        exportButton.removeAttribute('disabled');
      }
    }
  };

  // Remove fetchClients from the previous effect
  useEffect(() => {
    apiGet('/auth/me').then(res => {
      setCurrentUserId(res.id);
      if (selectedUserId === null) setSelectedUserId(res.id); // Only set default if not already set
    }).catch(() => {
      setCurrentUserId(null);
      if (selectedUserId === null) setSelectedUserId(null);
    });
    apiGet('/auth/users/hierarchy').then(setHierarchyUsers).catch(() => setHierarchyUsers([]));
  }, []);

  // Debounce name filter
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedNameFilter(nameFilter);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [nameFilter]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [debouncedNameFilter, lastStageFilter, intrareStartDate, intrareEndDate, lastStageStartDate, lastStageEndDate, selectedStelutaFilter, showAnulat, showContracted]);

  // Fetch clients when selectedUserId or favoritesOnly changes (no more server-side pagination)
  useEffect(() => {
    fetchClients();
  }, [selectedUserId, favoritesOnly]);

  const handleOpenModal = () => {
    setModalOpen(true);
    setHyperlink('');
    setExtractedData(null);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setHyperlink('');
    setExtractedData(null);
  };

  const handleStelutaChange = (clientId: number, columnIndex: number, value: string) => {
    const key = `${clientId}-${columnIndex}`;
    setStelutaValues(prev => ({
      ...prev,
      [key]: value
    }));
    
    // Auto-populate date if a value is selected
    let currentDate = getDateValue(clientId, columnIndex);
    if (value && !currentDate) {
      currentDate = new Date();
      const dateKey = `${clientId}-date-${columnIndex}`;
      setDateValues(prev => ({
        ...prev,
        [dateKey]: currentDate
      }));
    }

    // Map column index to stage type (skip 0 as it's handled by handleStelutaTextChange)
    const stageTypes = ['', 't1', 'planuri', 'preofertare', 'ofertare', 'contractare'];
    const stageType = stageTypes[columnIndex];
    
    if (stageType) {
      const column1 = value;
      const column2 = getCalificareValue(clientId, columnIndex);
      
      saveStageToDatabase(clientId, stageType, column1, column2, currentDate || undefined);
    }
  };

  const getStelutaValue = (clientId: number, columnIndex: number) => {
    const key = `${clientId}-${columnIndex}`;
    return stelutaValues[key] || '';
  };

  const handleStelutaTextChange = (clientId: number, columnIndex: number, value: string) => {
    const key = `${clientId}-text-${columnIndex}`;
    setStelutaTextValues(prev => ({
      ...prev,
      [key]: value
    }));
    // Auto-populate date if a value is selected
    let date = getDateValue(clientId, 0);
    if (value && !date) {
      date = new Date();
      const dateKey = `${clientId}-date-0`;
      setDateValues(prev => ({
        ...prev,
        [dateKey]: date
      }));
    }
    saveStageToDatabase(clientId, 'intrare', value, getCalificareNumberValue(clientId).toString(), date || undefined);
  };

  const getStelutaTextValue = (clientId: number, columnIndex: number) => {
    const key = `${clientId}-text-${columnIndex}`;
    return stelutaTextValues[key] || '';
  };

  const handleSemnareChange = (clientId: number, value: string) => {
    const key = `${clientId}-semnare`;
    setSemnareValues(prev => ({
      ...prev,
      [key]: value
    }));

    // This is for the contractare stage
    const column1 = value;
    const column2 = getIncasareValue(clientId);
    
    // Auto-populate date if a value is selected
    let date = getDateValue(clientId, 5);
    if (value && !date) {
      date = new Date();
      const dateKey = `${clientId}-date-5`;
      setDateValues(prev => ({
        ...prev,
        [dateKey]: date
      }));
    }
    
    saveStageToDatabase(clientId, 'contractare', column1, column2, date || undefined);
  };

  const getSemnareValue = (clientId: number) => {
    const key = `${clientId}-semnare`;
    return semnareValues[key] || '';
  };

  const handleIncasareChange = (clientId: number, value: string) => {
    const key = `${clientId}-incasare`;
    setIncasareValues(prev => ({
      ...prev,
      [key]: value
    }));

    // This is for the contractare stage
    const column1 = getSemnareValue(clientId);
    const column2 = value;
    
    // Auto-populate date if a value is selected
    let date = getDateValue(clientId, 5);
    if (value && !date) {
      date = new Date();
      const dateKey = `${clientId}-date-5`;
      setDateValues(prev => ({
        ...prev,
        [dateKey]: date
      }));
    }
    
    saveStageToDatabase(clientId, 'contractare', column1, column2, date || undefined);
  };

  const getIncasareValue = (clientId: number) => {
    const key = `${clientId}-incasare`;
    return incasareValues[key] || '';
  };

  const handleCalificareNumberChange = (clientId: number, value: string) => {
    const key = `${clientId}-calificare-number`;
    const numValue = value === '' ? 0 : parseFloat(value);
    setCalificareNumberValues(prev => ({
      ...prev,
      [key]: numValue
    }));
    // Auto-populate date if a value is selected
    let date = getDateValue(clientId, 0);
    if (numValue > 0 && !date) {
      date = new Date();
      const dateKey = `${clientId}-date-0`;
      setDateValues(prev => ({
        ...prev,
        [dateKey]: date
      }));
    }
    saveStageToDatabase(clientId, 'intrare', getStelutaTextValue(clientId, 0), numValue.toString(), date || undefined);
  };

  const getCalificareNumberValue = (clientId: number) => {
    const key = `${clientId}-calificare-number`;
    return calificareNumberValues[key] || 0;
  };

  const handleCalificareChange = (clientId: number, columnIndex: number, value: string) => {
    const key = `${clientId}-${columnIndex}`;
    setCalificareValues(prev => ({
      ...prev,
      [key]: value
    }));
    
    // Auto-populate date if a value is selected
    let currentDate = getDateValue(clientId, columnIndex);
    if (value && !currentDate) {
      currentDate = new Date();
      const dateKey = `${clientId}-date-${columnIndex}`;
      setDateValues(prev => ({
        ...prev,
        [dateKey]: currentDate
      }));
    }

    // Map column index to stage type (skip 0 as it's handled by handleCalificareNumberChange)
    const stageTypes = ['', 't1', 'planuri', 'preofertare', 'ofertare', 'contractare'];
    const stageType = stageTypes[columnIndex];
    
    if (stageType) {
      const column1 = getStelutaValue(clientId, columnIndex);
      const column2 = value;
      
      saveStageToDatabase(clientId, stageType, column1, column2, currentDate || undefined);
    }
  };

  const getCalificareValue = (clientId: number, columnIndex: number) => {
    const key = `${clientId}-${columnIndex}`;
    return calificareValues[key] || '';
  };

  const handleDateChange = (clientId: number, columnIndex: number, date: Date | null) => {
    const key = `${clientId}-date-${columnIndex}`;
    setDateValues(prev => ({
      ...prev,
      [key]: date
    }));

    // Map column index to stage type
    const stageTypes = ['intrare', 't1', 'planuri', 'preofertare', 'ofertare', 'contractare'];
    const stageType = stageTypes[columnIndex];
    
    // Get current values for column1 and column2
    let column1 = '';
    let column2 = '';
    
    if (columnIndex === 0) {
      // First stage: Sursa dropdown and Calificare number
      column1 = getStelutaTextValue(clientId, 0);
      column2 = getCalificareNumberValue(clientId).toString();
    } else {
      // Other stages: STELUTA dropdown and Calificare dropdown
      column1 = getStelutaValue(clientId, columnIndex);
      column2 = getCalificareValue(clientId, columnIndex);
    }
    
    // Save to database
    saveStageToDatabase(clientId, stageType, column1, column2, date || undefined);
  };

  const getDateValue = (clientId: number, columnIndex: number) => {
    const key = `${clientId}-date-${columnIndex}`;
    return dateValues[key] || null;
  };

  const saveStageToDatabase = async (clientId: number, stageType: string, column1: string, column2: string, date?: Date) => {
    try {
      const stageData = {
        clientId,
        stageType,
        column1,
        column2,
        date: date ? date.toISOString().split('T')[0] : '',
      };
      
      await apiPost('/client/stage', stageData);
    } catch (error: any) {
      console.error('Error saving stage:', error);
      // Don't show alert to avoid interrupting user workflow
    }
  };

  const readFromClipboard = async () => {
    try {
      const clipboardItems = await navigator.clipboard.read();
      
      if (clipboardItems.length > 0) {
        const clipboardItem = clipboardItems[0];
        
        if (clipboardItem.types.includes('text/html')) {
          const htmlBlob = await clipboardItem.getType('text/html');
          const htmlContent = await htmlBlob.text();
          
          // Parse HTML
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = htmlContent;
          
          // Extract URL using regex
          const urlMatch = htmlContent.match(/https?:\/\/[^\s"']+/);
          const url = urlMatch ? urlMatch[0] : 'No URL found';
          
          // Extract id_lucrare from URL
          let idLucrare = 'No ID found';
          if (url !== 'No URL found') {
            const idMatch = url.match(/id_lucrare[=:](\d+)/);
            idLucrare = idMatch ? idMatch[1] : 'No ID found';
          }
          
          // Get customer name from last child element
          const children = tempDiv.children;
          const customerName = children.length > 0 
            ? children[children.length - 1].textContent?.trim() || 'Unknown Customer'
            : 'Unknown Customer';
          
          const result = { customerName, url, idLucrare };
          setExtractedData(result);
          setHyperlink(htmlContent);
          
        } else {
          alert('Nu s-a găsit conținut HTML în clipboard. Copiați un hyperlink și încercați din nou.');
        }
      } else {
        alert('Clipboard-ul este gol. Copiați un hyperlink și încercați din nou.');
      }
    } catch (error) {
      console.error('Failed to read clipboard:', error);
      alert('Nu s-a putut citi din clipboard. Verificați permisiunile browserului.');
    }
  };

  const parseHyperlink = (text: string) => {
    try {
      // Parse HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = text;
      
      // Extract URL using regex
      const urlMatch = text.match(/https?:\/\/[^\s"']+/);
      const url = urlMatch ? urlMatch[0] : 'No URL found';
      
      // Extract id_lucrare from URL
      let idLucrare = 'No ID found';
      if (url !== 'No URL found') {
        const idMatch = url.match(/id_lucrare[=:](\d+)/);
        idLucrare = idMatch ? idMatch[1] : 'No ID found';
      }
      
      // Get customer name from last child element
      const children = tempDiv.children;
      const customerName = children.length > 0 
        ? children[children.length - 1].textContent?.trim() || 'Unknown Customer'
        : 'Unknown Customer';
      
      setExtractedData({ customerName, url, idLucrare });
    } catch (error) {
      console.error('Error parsing hyperlink:', error);
      setExtractedData({ customerName: 'Error parsing', url: 'Error parsing', idLucrare: 'Error parsing' });
    }
  };

  const handleSubmitHyperlink = async () => {
    // Prefer manual fields if filled
    let name = manualName.trim();
    let url = manualUrl.trim();
    let idLucrare = '';
    setManualError('');

    if (name || url) {
      // Manual entry
      if (!name || !url) {
        setManualError('Introduceți numele clientului și URL-ul.');
        return;
      }
      const idMatch = url.match(/id_lucrare[=:](\d+)/);
      if (!idMatch) {
        setManualError('URL-ul trebuie să conțină id_lucrare.');
        return;
      }
      idLucrare = idMatch[1];
    } else if (extractedData) {
      // Use extracted data
      name = extractedData.customerName;
      url = extractedData.url;
      idLucrare = extractedData.idLucrare;
      if (!name || !url || !idLucrare || idLucrare === 'No ID found') {
        setManualError('Datele extrase nu sunt valide.');
        return;
      }
    } else {
      setManualError('Completați manual sau folosiți clipboard-ul.');
      return;
    }
    try {
      const clientData = {
        name,
        jobId: idLucrare,
        url,
      };
      await apiPost('/client', clientData);
      await fetchClients();
      handleCloseModal();
      setManualName('');
      setManualUrl('');
    } catch (error: any) {
      setManualError(`Eroare la salvare: ${error.message}`);
    }
  };

  const handleManualSubmit = async () => {
    setManualError('');
    if (!manualName.trim() || !manualUrl.trim()) {
      setManualError('Introduceți numele clientului și URL-ul.');
      return;
    }
    // Extract id_lucrare from URL
    const idMatch = manualUrl.match(/id_lucrare[=:](\d+)/);
    if (!idMatch) {
      setManualError('URL-ul trebuie să conțină id_lucrare.');
      return;
    }
    const idLucrare = idMatch[1];
    try {
      const clientData = {
        name: manualName,
        jobId: idLucrare,
        url: manualUrl,
      };
      await apiPost('/client', clientData);
      await fetchClients();
      handleCloseModal();
      setManualName('');
      setManualUrl('');
    } catch (error: any) {
      setManualError(`Eroare la salvare: ${error.message}`);
    }
  };

  const handleToggleFavorite = async (clientId: number) => {
    try {
      const result = await apiPost(`/client/${clientId}/favorite`, {});
      setFavoritesStatus(prev => ({
        ...prev,
        [clientId]: result.isFavorite
      }));
    } catch (err: any) {
      console.error('Error toggling favorite:', err);
    }
  };

  const openCommentModal = async (clientId: number) => {
    setCommentClientId(clientId);
    setCommentModalOpen(true);
    setCommentLoading(true);
    setCommentError(null);
    try {
      const data = await apiGet(`/client/${clientId}/comments`);
      setComments(data);
    } catch (err: any) {
      setCommentError('Eroare la încărcarea comentariilor');
      setComments([]);
    } finally {
      setCommentLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!commentClientId || !newComment.trim()) return;
    setCommentLoading(true);
    setCommentError(null);
    try {
      await apiPost(`/client/${commentClientId}/comment`, { comment: newComment });
      setNewComment('');
      // Refresh comments
      const data = await apiGet(`/client/${commentClientId}/comments`);
      setComments(data);
    } catch (err: any) {
      setCommentError('Eroare la adăugarea comentariului');
    } finally {
      setCommentLoading(false);
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    if (!window.confirm('Sigur doriți să ștergeți acest comentariu?')) return;
    setCommentLoading(true);
    setCommentError(null);
    try {
      await apiPost(`/client/comment/${commentId}`, {}, 'DELETE');
      // Refresh comments
      if (commentClientId) {
        const data = await apiGet(`/client/${commentClientId}/comments`);
        setComments(data);
      }
    } catch (err: any) {
      setCommentError('Eroare la ștergerea comentariului');
    } finally {
      setCommentLoading(false);
    }
  };

  const handleCommentButtonHover = async (clientId: number) => {
    setHoveredClientId(clientId);
    setHoverLoading(true);
    try {
      const data = await apiGet(`/client/${clientId}/comments`);
      setHoverComments(data);
    } catch {
      setHoverComments([]);
    } finally {
      setHoverLoading(false);
    }
  };

  const handleSyncClients = async () => {
    if (!window.confirm('Sigur doriți să sincronizați clienții?')) {
      return;
    }
    
    setSyncing(true);
    try {
      const response = await apiPost('/client/sync');
      if (response.success) {
        alert(`Sincronizare completă! ${response.syncedCount || 0} clienți sincronizați.`);
        await fetchClients(); // Refresh the client list
      } else {
        alert(`Eroare la sincronizare: ${response.message}`);
      }
    } catch (error: any) {
      alert(`Eroare la sincronizare: ${error.message}`);
    } finally {
      setSyncing(false);
    }
  };

  /*
   .MuiBox-root{
              zoom: 90%;
            }
  */

  // Filter clients based on all filters (client-side)
  const filteredClients = useMemo(() => {
    let filtered = clients;

    // Name filter
    if (debouncedNameFilter.trim()) {
      filtered = filtered.filter(client => 
        client.name.toLowerCase().includes(debouncedNameFilter.toLowerCase())
      );
    }

    // Last stage filter
    if (lastStageFilter.trim()) {
      filtered = filtered.filter(client => {
        if (!client.stages || client.stages.length === 0) return false;
        // Find the last modified stage (by updatedAt)
        const sortedStages = [...client.stages].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        const lastStage = sortedStages[0];
        return lastStage && lastStage.stageType === lastStageFilter;
      });
    }

    // Intrare stage date range filters
    if (intrareStartDate) {
      filtered = filtered.filter(client => {
        const intrareStage = client.stages.find(stage => stage.stageType === 'intrare');
        if (!intrareStage || !intrareStage.date) return false;
        const stageDate = new Date(intrareStage.date);
        return stageDate >= intrareStartDate;
      });
    }
    if (intrareEndDate) {
      filtered = filtered.filter(client => {
        const intrareStage = client.stages.find(stage => stage.stageType === 'intrare');
        if (!intrareStage || !intrareStage.date) return false;
        const stageDate = new Date(intrareStage.date);
        return stageDate <= intrareEndDate;
      });
    }

    // Last stage date range filters
    if (lastStageStartDate) {
      filtered = filtered.filter(client => {
        if (!client.stages || client.stages.length === 0) return false;
        const sortedStages = [...client.stages].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        const lastStage = sortedStages[0];
        if (!lastStage || !lastStage.date) return false;
        const stageDate = new Date(lastStage.date);
        return stageDate >= lastStageStartDate;
      });
    }
    if (lastStageEndDate) {
      filtered = filtered.filter(client => {
        if (!client.stages || client.stages.length === 0) return false;
        const sortedStages = [...client.stages].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        const lastStage = sortedStages[0];
        if (!lastStage || !lastStage.date) return false;
        const stageDate = new Date(lastStage.date);
        return stageDate <= lastStageEndDate;
      });
    }

    // Star filter
    if (!selectedStelutaFilter.includes('normal')) {
      filtered = filtered.filter(client => {
        if (!client.stages || client.stages.length === 0) return false;
        // Find the last modified stage (by updatedAt)
        const sortedStages = [...client.stages].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        const lastStage = sortedStages[0];
        // Only include if the last modified stage has a star and matches any of the selected stars
        return lastStage && lastStage.column1 && lastStage.column1 !== '' && selectedStelutaFilter.includes(lastStage.column1);
      });
    }

    // Anulat filter
    if (!showAnulat) {
      filtered = filtered.filter(client => {
        return !client.stages.some(stage => stage.column2 === 'anulat');
      });
    }

    // Contracted filter
    if (!showContracted) {
      filtered = filtered.filter(client => {
        const contractareStage = client.stages.find(stage => stage.stageType === 'contractare');
        if (!contractareStage) return true;
        return contractareStage.column1 !== 'DA' && contractareStage.column2 !== 'DA';
      });
    }

    return filtered;
  }, [clients, debouncedNameFilter, lastStageFilter, intrareStartDate, intrareEndDate, lastStageStartDate, lastStageEndDate, selectedStelutaFilter, showAnulat, showContracted]);

  // Helper to get ordered stage types
  const STAGE_ORDER = [
    ClientStageType.INTRARE,
    ClientStageType.REVENIRI,
    ClientStageType.PLANURI,
    ClientStageType.PREOFERTARE,
    ClientStageType.OFERTARE,
    ClientStageType.CONTRACTARE,
  ];

  // Helper to get last completed stage (with a star) and next stage
  function getLastAndNextStage(stages: ClientStage[]): { lastCompleted: ClientStage | null, nextType: ClientStageType | null } {
    let lastCompleted: ClientStage | null = null;
    let lastCompletedIndex = 0;
    for (let i = 1; i < STAGE_ORDER.length; i++) {
      const stage = stages.find(s => s.stageType === STAGE_ORDER[i]);
      if (stage && stage.column1 && stage.column1 !== '') {
        lastCompleted = stage;
        lastCompletedIndex = i;
      }
    }
    // If no completed, next is REVENIRI
    let nextType: ClientStageType | null = null;
    if (lastCompletedIndex < STAGE_ORDER.length - 1) {
      nextType = STAGE_ORDER[lastCompletedIndex + 1];
    }
    return { lastCompleted, nextType };
  }

  // Add mapping for stage type to label and icon
  const STAGE_LABELS: Record<ClientStageType, string> = {
    [ClientStageType.INTRARE]: 'Intrare',
    [ClientStageType.REVENIRI]: 'T1',
    [ClientStageType.PLANURI]: 'Schita / Planuri',
    [ClientStageType.PREOFERTARE]: 'PreOfertare',
    [ClientStageType.OFERTARE]: 'Ofertare',
    [ClientStageType.CONTRACTARE]: 'Contractare',
  };
  const STAGE_ICONS: Record<ClientStageType, React.ReactNode> = {
    [ClientStageType.INTRARE]: <InputIcon fontSize="small" sx={{ mr: 0.5 }} />,
    [ClientStageType.REVENIRI]: <ReplayIcon fontSize="small" sx={{ mr: 0.5 }} />,
    [ClientStageType.PLANURI]: <DesignServicesIcon fontSize="small" sx={{ mr: 0.5 }} />,
    [ClientStageType.PREOFERTARE]: <LocalOfferIcon fontSize="small" sx={{ mr: 0.5 }} />,
    [ClientStageType.OFERTARE]: <AttachMoneyIcon fontSize="small" sx={{ mr: 0.5 }} />,
    [ClientStageType.CONTRACTARE]: <AssignmentTurnedInIcon fontSize="small" sx={{ mr: 0.5 }} />,
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <Box>
        <style>
          {`
            .MuiPickersSectionList-root  {
              font-size: 0.75rem !important;
            }
           
          
            .MuiSelect-select {
              padding: 2px !important;
            }
            .MuiInputBase-root{
              text-align: center !important;
            }
            input[type="number"]::-webkit-outer-spin-button,
            input[type="number"]::-webkit-inner-spin-button {
              -webkit-appearance: none;
              margin: 0;
            }
            input[type="number"] {
              -moz-appearance: textfield;
            }
          `}
        </style>
        <Card>
        <CardContent>
          <Box sx={{ mb: 2 }}>
            {/* Header Row */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={filtersExpanded ? 2 : 0}
            >
              <Typography variant="h5" fontWeight={700}>
                Palnie Clienti
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                {/* Filter Toggle Button */}
                <Button
                  variant="outlined"
                  startIcon={<FilterListIcon />}
                  endIcon={filtersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  onClick={() => setFiltersExpanded(!filtersExpanded)}
                  sx={{ 
                    borderColor: '#BA1010', 
                    color: '#BA1010', 
                    '&:hover': { backgroundColor: '#BA1010', color: 'white' },
                    minWidth: 120
                  }}
                >
                  Filtre
                </Button>
                
                {/* Action Buttons */}
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleOpenModal}
                  sx={{ backgroundColor: '#BA1010', '&:hover': { backgroundColor: '#8B0A0A' } }}
                >
                  Adaugă Client
                </Button>
                <Button
                  variant="outlined"
                  startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
                  onClick={handleSyncClients}
                  disabled={syncing}
                  sx={{ borderColor: '#BA1010', color: '#BA1010', '&:hover': { backgroundColor: '#BA1010', color: 'white' } }}
                >
                  {syncing ? 'Sincronizare...' : 'Sincronizează'}
                </Button>
              </Box>
            </Box>
            
            {/* Collapsible Filters Section */}
            <Collapse in={filtersExpanded} timeout="auto" unmountOnExit>
              <Box 
                sx={{ 
                  p: 2, 
                  backgroundColor: '#f8f9fa', 
                  borderRadius: 2, 
                  border: '1px solid #e9ecef',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  alignItems: 'flex-end'
                }}
              >
                {/* Basic Filters Group */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 200 }}>
                  <Typography variant="caption" fontWeight={600} color="text.secondary">Filtre de bază</Typography>
                  <FormControl size="small" sx={{ minWidth: 200, background: 'white', borderRadius: 2 }}>
                    <InputLabel>Select User</InputLabel>
                    <Select
                      value={selectedUserId || ''}
                      onChange={(e) => setSelectedUserId(e.target.value ? Number(e.target.value) : null)}
                      label="Select User"
                      sx={{
                        fontSize: '0.95rem',
                        borderRadius: 2,
                        background: 'white',
                        height: 36,
                        minHeight: 36,
                        '.MuiSelect-select': {
                          padding: '6px 10px',
                          display: 'flex',
                          alignItems: 'center',
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: { borderRadius: 2 }
                        }
                      }}
                    >
                      {hierarchyUsers.map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                          <span style={{ marginLeft: 8 }}>{user.username} (Level {user.level})</span>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Box display="flex" alignItems="center" gap={1}>
                    <TextField
                      size="small"
                      placeholder="Filtrează după nume..."
                      value={nameFilter}
                      onChange={(e) => setNameFilter(e.target.value)}
                      sx={{
                        minWidth: 200,
                        background: 'white',
                        borderRadius: 2,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          background: 'white',
                          height: 36,
                          fontSize: '0.95rem',
                          '& input': {
                            padding: '6px 10px',
                            fontSize: '0.95rem',
                          },
                        },
                      }}
                    />
                    {nameFilter && (
                      <IconButton
                        size="small"
                        onClick={() => setNameFilter('')}
                        sx={{
                          color: '#666',
                          '&:hover': { color: '#333' }
                        }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    )}
                     
                  
                  <FormControl size="small" sx={{ minWidth: 180, background: 'white', borderRadius: 2 }}>
                    <InputLabel>Ultima etapă</InputLabel>
                    <Select
                      value={lastStageFilter}
                      onChange={(e) => setLastStageFilter(e.target.value)}
                      label="Ultima etapă"
                      sx={{
                        fontSize: '0.95rem',
                        borderRadius: 2,
                        background: 'white',
                        height: 36,
                        minHeight: 36,
                        '.MuiSelect-select': {
                          padding: '6px 10px',
                          display: 'flex',
                          alignItems: 'center',
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: { borderRadius: 2 }
                        }
                      }}
                    >
                      {STAGE_FILTER_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  </Box>
                </Box>
                
            
                
                {/* Star Filter Group */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 200 }}>
                  <Typography variant="caption" fontWeight={600} color="text.secondary">Filtru stele</Typography>
                  <FormControl size="small" sx={{ minWidth: 200, background: 'white', borderRadius: 2 }}>
                                         <Select
                       multiple
                       value={selectedStelutaFilter}
                       onChange={(e) => {
                         const value = e.target.value;
                         
                         // Handle both string and string[] cases
                         if (typeof value === 'string') {
                           setSelectedStelutaFilter([value]);
                         } else if (Array.isArray(value)) {
                           // Filter out 'normal' if other values are selected, and ensure all values are strings
                           const filteredValues = value
                             .filter(v => v !== 'normal' || value.length === 1)
                             .map(v => String(v));
                           setSelectedStelutaFilter(filteredValues.length > 0 ? filteredValues : ['normal']);
                         }
                       }}
                       displayEmpty
                       IconComponent={() => null}
                      sx={{
                        fontSize: '0.95rem',
                        borderRadius: 2,
                        background: 'white',
                        height: 36,
                        minHeight: 36,
                        '.MuiSelect-select': {
                          padding: '6px 10px',
                          display: 'flex',
                          alignItems: 'center',
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: { borderRadius: 2 }
                        }
                      }}
                       renderValue={(selected) => {
                         const valuesToRender = selectedStelutaFilter;
                         
                         if (valuesToRender.length === 0) {
                           return <em style={{ color: '#666' }}>Normal</em>;
                         }
                         if (valuesToRender.length === 1 && valuesToRender[0] === 'normal') {
                           return <em style={{ color: '#666' }}>Normal</em>;
                         }
                         if (valuesToRender.length === 1) {
                           const option = STELUTA_FILTER_OPTIONS.find(opt => opt.value === valuesToRender[0]);
                           return option ? option.label : String(valuesToRender[0]);
                         }
                         return <em style={{ color: '#666' }}>Multiple</em>;
                       }}
                    >
                      {STELUTA_FILTER_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          <Box display="flex" alignItems="center" gap={1}>
                            {option.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                
                {/* Date Filters Group */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 300 }}>
                  <Typography variant="caption" fontWeight={600} color="text.secondary">Filtre de dată</Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="caption" sx={{ whiteSpace: 'nowrap', minWidth: 80 }}>Intrare:</Typography>
                    <DatePicker
                      formatDensity='dense'
                      value={intrareStartDate}
                      onChange={(date) => setIntrareStartDate(date)}
                      slotProps={{
                        textField: {
                          size: 'small',
                          placeholder: 'De la',
                          sx: {
                            minWidth: 120,
                            background: 'white',
                            borderRadius: 2,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              background: 'white',
                              height: 36,
                              fontSize: '0.95rem',
                              '& input': {
                                padding: '6px 10px',
                                fontSize: '0.95rem',
                              },
                            },
                          },
                        }
                      }}
                      format="dd/MM/yyyy"
                    />
                    <Typography variant="caption" sx={{ mx: 1 }}>-</Typography>
                    <DatePicker
                      formatDensity='dense'
                      value={intrareEndDate}
                      onChange={(date) => setIntrareEndDate(date)}
                      slotProps={{
                        textField: {
                          size: 'small',
                          placeholder: 'Până la',
                          sx: {
                            minWidth: 120,
                            background: 'white',
                            borderRadius: 2,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              background: 'white',
                              height: 36,
                              fontSize: '0.95rem',
                              '& input': {
                                padding: '6px 10px',
                                fontSize: '0.95rem',
                              },
                            },
                          },
                        }
                      }}
                      format="dd/MM/yyyy"
                    />
                  </Box>
                  
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="caption" sx={{ whiteSpace: 'nowrap', minWidth: 80 }}>Ultima etapă:</Typography>
                    <DatePicker
                      formatDensity='dense'
                      value={lastStageStartDate}
                      onChange={(date) => setLastStageStartDate(date)}
                      slotProps={{
                        textField: {
                          size: 'small',
                          placeholder: 'De la',
                          sx: {
                            minWidth: 120,
                            background: 'white',
                            borderRadius: 2,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              background: 'white',
                              height: 36,
                              fontSize: '0.95rem',
                              '& input': {
                                padding: '6px 10px',
                                fontSize: '0.95rem',
                              },
                            },
                          },
                        }
                      }}
                      format="dd/MM/yyyy"
                    />
                    <Typography variant="caption" sx={{ mx: 1 }}>-</Typography>
                    <DatePicker
                      formatDensity='dense'
                      value={lastStageEndDate}
                      onChange={(date) => setLastStageEndDate(date)}
                      slotProps={{
                        textField: {
                          size: 'small',
                          placeholder: 'Până la',
                          sx: {
                            minWidth: 120,
                            background: 'white',
                            borderRadius: 2,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              background: 'white',
                              height: 36,
                              fontSize: '0.95rem',
                              '& input': {
                                padding: '6px 10px',
                                fontSize: '0.95rem',
                              },
                            },
                          },
                        }
                      }}
                      format="dd/MM/yyyy"
                    />
                  </Box>
                </Box>
                
                {/* Quick Actions Group */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 200 }}>
                  <Typography variant="caption" fontWeight={600} color="text.secondary">Acțiuni rapide</Typography>
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <input
                        type="checkbox"
                        id="showAnulat"
                        checked={showAnulat}
                        onChange={(e) => setShowAnulat(e.target.checked)}
                        style={{ margin: 0 }}
                      />
                      <label htmlFor="showAnulat" style={{ fontSize: '0.875rem', cursor: 'pointer' }}>
                        Arată anulate
                      </label>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <input
                        type="checkbox"
                        id="showContracted"
                        checked={showContracted}
                        onChange={(e) => setShowContracted(e.target.checked)}
                        style={{ margin: 0 }}
                      />
                      <label htmlFor="showContracted" style={{ fontSize: '0.875rem', cursor: 'pointer' }}>
                        Arată contractate
                      </label>
                    </Box>
                    <Button
                      variant={favoritesOnly ? "contained" : "outlined"}
                      startIcon={<StarIcon />}
                      onClick={() => setFavoritesOnly(!favoritesOnly)}
                      size="small"
                      sx={{ 
                        backgroundColor: favoritesOnly ? '#BA1010' : 'transparent',
                        color: favoritesOnly ? 'white' : '#BA1010',
                        borderColor: '#BA1010',
                        '&:hover': { 
                          backgroundColor: favoritesOnly ? '#8B0A0A' : '#BA1010',
                          color: 'white'
                        },
                        alignSelf: 'flex-start',
                        mt: 0.5
                      }}
                    >
                      {favoritesOnly ? 'Toate' : 'Favorite'}
                    </Button>
                  </Box>
                </Box>
                
                {/* Clear Filters Button */}
                                 {(nameFilter || lastStageFilter || intrareStartDate || intrareEndDate || lastStageStartDate || lastStageEndDate || !selectedStelutaFilter.includes('normal')) && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      startIcon={<ClearIcon />}
                                             onClick={() => {
                         setNameFilter('');
                         setLastStageFilter('');
                         setIntrareStartDate(null);
                         setIntrareEndDate(null);
                         setLastStageStartDate(null);
                         setLastStageEndDate(null);
                         setSelectedStelutaFilter(['normal']);
                       }}
                      sx={{ borderColor: '#666', color: '#666', '&:hover': { backgroundColor: '#666', color: 'white' } }}
                    >
                      Curăță filtrele
                    </Button>
                  </Box>
                )}
              </Box>
            </Collapse>
          </Box>
          
          {/* Export to PDF Button - Same level as FILTRE */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Button
              id="export-pdf-btn"
              variant="contained"
              startIcon={<PictureAsPdfIcon />}
              onClick={exportToPDF}
              disabled={loading}
              sx={{ 
                backgroundColor: '#d32f2f', 
                '&:hover': { backgroundColor: '#b71c1c' },
                color: 'white'
              }}
            >
              Export PDF
            </Button>
          </Box>
          
          {loading && (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!loading && !error && (
            <Box id="client-pipeline-container">
              {clients.length === 0 ? (
                <Typography variant="body1" color="text.secondary" textAlign="center" py={3}>
                  Nu există clienți încă. Adaugă primul client pentru a începe.
                </Typography>
              ) : (
                <>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, borderRight: 2, borderColor: 'grey.400', padding: '8px 4px', fontSize: '0.675rem', minWidth: 220, maxWidth: 320 }}>Nume Client</TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: 'center', borderRight: 2, borderColor: 'grey.400', padding: '8px 4px', fontSize: '0.675rem', width: '33.33%' }}>Intrare</TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: 'center', borderRight: 2, borderColor: 'grey.400', padding: '8px 4px', fontSize: '0.675rem', width: '33.33%' }}>Ultima etapă completă</TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: 'center', padding: '8px 4px', fontSize: '0.675rem', width: '33.33%' }}>Etapa următoare</TableCell>
                      </TableRow>
                    </TableHead>
                                                              <TableBody>
                        {filteredClients
                          .slice((page - 1) * limit, page * limit)
                          .map((client) => {
                        // Intrare stage
                        const intrareStage = client.stages.find(s => s.stageType === ClientStageType.INTRARE);
                        // Last completed and next stage
                        const { lastCompleted, nextType } = getLastAndNextStage(client.stages);
                        const nextStage = nextType ? client.stages.find(s => s.stageType === nextType) : null;
                        // Helper to get column index for a stage type
                        const getColumnIndex = (stageType: ClientStageType) => STAGE_ORDER.indexOf(stageType);
                        // Row coloring logic
                        const hasAnulat = client.stages.some(stage => stage.column2 === 'anulat');
                        const contractareStage = client.stages.find(stage => stage.stageType === 'contractare');
                        const isContracted = contractareStage && (contractareStage.column1 === 'DA' || contractareStage.column2 === 'DA');
                        return (
                          <TableRow
                            key={client.id}
                            hover
                            sx={{
                              backgroundColor: isContracted ? '#e8f5e8' : (hasAnulat ? '#ffebee' : 'inherit'),
                              '&:hover': {
                                backgroundColor: isContracted ? '#d4edda' : (hasAnulat ? '#ffcdd2' : undefined)
                              }
                            }}
                          >
                            <TableCell sx={{ borderRight: 2, borderColor: 'grey.400', padding: '8px 4px' }}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Button
                                  size="small"
                                  onClick={() => handleToggleFavorite(client.id)}
                                  sx={{
                                    minWidth: 'auto',
                                    p: 0.5,
                                    color: favoritesStatus[client.id] ? '#FFD700' : '#ccc',
                                    '&:hover': {
                                      color: favoritesStatus[client.id] ? '#FFA500' : '#FFD700',
                                      backgroundColor: 'transparent'
                                    }
                                  }}
                                >
                                  <StarIcon fontSize="small" />
                                </Button>
                                <Tooltip
  title={
    hoverLoading && hoveredClientId === client.id ? (
      <Box p={1}><CircularProgress size={16} /></Box>
    ) : hoverComments.length === 0 && hoveredClientId === client.id ? (
      <Typography variant="caption">Nu există comentarii</Typography>
    ) : hoveredClientId === client.id ? (
      <Box>
        {hoverComments.map((c, idx) => (
          <Box key={c.comment_id || idx} mb={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="caption" fontWeight={600}>{c.user_username || c.userId}</Typography>
              <Typography variant="caption" >
                {c.comment_createdAt ? new Date(c.comment_createdAt).toLocaleString() : ''}
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>{c.comment_comment}</Typography>
          </Box>
        ))}
      </Box>
    ) : ''
  }
  arrow
  placement="right"
  onOpen={() => handleCommentButtonHover(client.id)}
  onClose={() => { setHoveredClientId(null); setHoverComments([]); }}
>
  <Button
    size="small"
    sx={{ minWidth: 'auto', p: 0.5 }}
    title="Comentarii"
    onClick={() => openCommentModal(client.id)}
  >
    <CommentIcon fontSize="small" />
  </Button>
</Tooltip>
                                {client.url && client.url !== 'No URL found' ? (
                                  <a 
                                    href={client.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    style={{ 
                                      color: '#BA1010', 
                                      textDecoration: 'none',
                                      fontWeight: 500
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.textDecoration = 'underline';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.textDecoration = 'none';
                                    }}
                                  >
                                    {client.name}
                                  </a>
                                ) : (
                                  client.name
                                )}
                              </Box>
                            </TableCell>
                            {/* Intrare (editable) */}
                            <TableCell sx={{ borderRight: 2, borderColor: 'grey.400', padding: '8px 4px', width: '33.33%' }}>
                              <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'flex', alignItems: 'center' }}>{STAGE_ICONS[ClientStageType.INTRARE]}{STAGE_LABELS[ClientStageType.INTRARE]}</Typography>
                              <Box display="flex" flexDirection="row" gap={1}>
                                <Box sx={{ flex: 2, minWidth: 120, display: 'flex', flexDirection: 'column' }}>
                                  <Typography variant="caption" sx={{ ml: 0.5, mb: 0.5 }}>Data</Typography>
                                  <DatePicker
                                    formatDensity='dense'
                                    value={getDateValue(client.id, 0)}
                                    onChange={(date) => handleDateChange(client.id, 0, date)}
                                    slotProps={{
                                      textField: {
                                        size: 'small',
                                        fullWidth: true,
                                        sx: {
                                          minWidth: 90,
                                          maxWidth: 140,
                                          p: 0,
                                          background: '#f7f7fa',
                                          borderRadius: 2,
                                          fontSize: '0.95rem',
                                          '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            background: '#f7f7fa',
                                            fontSize: '0.95rem',
                                            height: 36,
                                          },
                                          '& input': {
                                            fontSize: '0.95rem',
                                            padding: '6px 10px',
                                          },
                                        },
                                        InputProps: {
                                          sx: {
                                            borderRadius: 2,
                                            background: '#f7f7fa',
                                            fontSize: '0.95rem',
                                          },
                                        },
                                        onClick: (e: React.MouseEvent<HTMLInputElement>) => {
                                          const button = e.currentTarget.parentElement?.querySelector('button');
                                          if (button) (button as HTMLButtonElement).click();
                                        },
                                      }
                                    }}
                                    format="dd/MM/yyyy"
                                  />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="caption" sx={{ ml: 0.5 }}>Sursa</Typography>
                                  <TextField
                                    size="small"
                                    fullWidth
                                    value={getStelutaTextValue(client.id, 0)}
                                    onChange={(e) => handleStelutaTextChange(client.id, 0, e.target.value)}
                                    sx={{
                                      fontSize: '0.95rem',
                                      borderRadius: 2,
                                      background: '#f7f7fa',
                                      height: 36,
                                      minHeight: 36,
                                      '& .MuiOutlinedInput-root': {
                                        height: 36,
                                        minHeight: 36,
                                        borderRadius: 2,
                                        background: '#f7f7fa',
                                        '& fieldset': {
                                          border: 'none',
                                        },
                                        '& input': {
                                          padding: '6px 10px',
                                          fontSize: '0.95rem',
                                        },
                                      },
                                    }}
                                  />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="caption" sx={{ ml: 0.5 }}>Suprafata</Typography>
                                  <TextField
                                    type="number"
                                    size="small"
                                    fullWidth
                                    value={getCalificareNumberValue(client.id)}
                                    onChange={(e) => handleCalificareNumberChange(client.id, e.target.value)}
                                    sx={{
                                      fontSize: '0.95rem',
                                      borderRadius: 2,
                                      background: '#f7f7fa',
                                      '& .MuiInputBase-root': {
                                        borderRadius: 2,
                                        background: '#f7f7fa',
                                        fontSize: '0.95rem',
                                        height: 36,
                                      },
                                      '& .MuiInputBase-input': {
                                        fontSize: '0.95rem',
                                        padding: '6px 10px',
                                      },
                                    }}
                                    InputProps={{
                                      sx: {
                                        borderRadius: 2,
                                        background: '#f7f7fa',
                                        fontSize: '0.95rem',
                                      },
                                    }}
                                  />
                                </Box>
                              </Box>
                            </TableCell>
                            {/* Last completed stage (editable) */}
                            <TableCell sx={{ borderRight: 2, borderColor: 'grey.400', padding: '8px 4px', width: '33.33%' }}>
                              {lastCompleted ? (
                              <>
                              <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'flex', alignItems: 'center' }}>{STAGE_ICONS[lastCompleted.stageType]}{STAGE_LABELS[lastCompleted.stageType]}</Typography>
                                <Box display="flex" flexDirection="row" gap={1}>
                                 
                                  <Box sx={{ flex: 2, minWidth: 120, display: 'flex', flexDirection: 'column' }}>
                                  <Typography variant="caption" sx={{ ml: 0.5, mb: 0.5 }}>Data</Typography>
                                  <DatePicker
                                    formatDensity='dense'
                                    value={getDateValue(client.id, getColumnIndex(lastCompleted.stageType))}
                                    onChange={(date) => handleDateChange(client.id, getColumnIndex(lastCompleted.stageType), date)}
                                    slotProps={{
                                      textField: {
                                        size: 'small',
                                        fullWidth: true,
                                        sx: {
                                          minWidth: 90,
                                          maxWidth: 140,
                                          p: 0,
                                          background: '#f7f7fa',
                                          borderRadius: 2,
                                          fontSize: '0.95rem',
                                          '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            background: '#f7f7fa',
                                            fontSize: '0.95rem',
                                            height: 36,
                                          },
                                          '& input': {
                                            fontSize: '0.95rem',
                                            padding: '6px 10px',
                                          },
                                        },
                                        InputProps: {
                                          sx: {
                                            borderRadius: 2,
                                            background: '#f7f7fa',
                                            fontSize: '0.95rem',
                                          },
                                        },
                                        onClick: (e: React.MouseEvent<HTMLInputElement>) => {
                                          const button = e.currentTarget.parentElement?.querySelector('button');
                                          if (button) (button as HTMLButtonElement).click();
                                        },
                                      }
                                    }}
                                    format="dd/MM/yyyy"
                                  />
                                </Box>
                                  {/* Contractare special UI */}
                                  {lastCompleted.stageType === ClientStageType.CONTRACTARE ? (
                                    <>
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography variant="caption" sx={{ ml: 0.5 }}>Semnare</Typography>
                                      <FormControl size="small" fullWidth sx={{ background: '#f7f7fa', borderRadius: 2 }}>
                                        <Select
                                          value={getSemnareValue(client.id)}
                                          onChange={(e) => handleSemnareChange(client.id, e.target.value)}
                                          displayEmpty
                                          sx={{
                                            fontSize: '0.95rem',
                                            borderRadius: 2,
                                            background: '#f7f7fa',
                                            height: 36,
                                            minHeight: 36,
                                            '.MuiSelect-select': {
                                              padding: '6px 10px',
                                            },
                                          }}
                                          MenuProps={{
                                            PaperProps: {
                                              sx: { borderRadius: 2 }
                                            }
                                          }}
                                        >
                                          <MenuItem value=""><em>-</em></MenuItem>
                                          {DA_NU_OPTIONS.map(opt => (
                                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography variant="caption" sx={{ ml: 0.5 }}>Incasare</Typography>
                                      <FormControl size="small" fullWidth sx={{ background: '#f7f7fa', borderRadius: 2 }}>
                                        <Select
                                          value={getIncasareValue(client.id)}
                                          onChange={(e) => handleIncasareChange(client.id, e.target.value)}
                                          displayEmpty
                                          sx={{
                                            fontSize: '0.95rem',
                                            borderRadius: 2,
                                            background: '#f7f7fa',
                                            height: 36,
                                            minHeight: 36,
                                            '.MuiSelect-select': {
                                              padding: '6px 10px',
                                            },
                                          }}
                                          MenuProps={{
                                            PaperProps: {
                                              sx: { borderRadius: 2 }
                                            }
                                          }}
                                        >
                                          <MenuItem value=""><em>-</em></MenuItem>
                                          {DA_NU_OPTIONS.map(opt => (
                                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                    </Box>
                                    </>
                                  ) : (
                                    <>
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography variant="caption" sx={{ ml: 0.5 }}>STELUTA</Typography>
                                      <FormControl size="small" fullWidth sx={{ background: '#f7f7fa', borderRadius: 2 }}>
                                        <Select
                                          IconComponent={() => null}
                                          value={getStelutaValue(client.id, getColumnIndex(lastCompleted.stageType))}
                                          onChange={(e) => handleStelutaChange(client.id, getColumnIndex(lastCompleted.stageType), e.target.value)}
                                          displayEmpty
                                          sx={{
                                            fontSize: '0.95rem',
                                            borderRadius: 2,
                                            background: '#f7f7fa',
                                            height: 36,
                                            minHeight: 36,
                                            '.MuiSelect-select': {
                                              padding: '6px 10px',
                                            },
                                          }}
                                          MenuProps={{
                                            PaperProps: {
                                              sx: { borderRadius: 2 }
                                            }
                                          }}
                                        >
                                          <MenuItem value="">
                                            <em>-</em>
                                          </MenuItem>
                                          {STELUTA_OPTIONS.map((option, index) => (
                                            <MenuItem key={index} value={option.value}>
                                              <Tooltip title={option.tooltip} placement="top">
                                                {option.label}
                                              </Tooltip>
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography variant="caption" sx={{ ml: 0.5 }}>Calificare</Typography>
                                      <FormControl size="small" fullWidth sx={{ background: '#f7f7fa', borderRadius: 2 }}>
                                        <Select
                                          IconComponent={() => null}
                                          value={getCalificareValue(client.id, getColumnIndex(lastCompleted.stageType))}
                                          onChange={(e) => handleCalificareChange(client.id, getColumnIndex(lastCompleted.stageType), e.target.value)}
                                          displayEmpty
                                          sx={{
                                            fontSize: '0.95rem',
                                            borderRadius: 2,
                                            background: '#f7f7fa',
                                            height: 36,
                                            minHeight: 36,
                                            '.MuiSelect-select': {
                                              padding: '6px 10px',
                                            },
                                          }}
                                          MenuProps={{
                                            PaperProps: {
                                              sx: { borderRadius: 2 }
                                            }
                                          }}
                                        >
                                          <MenuItem value="">
                                            <em>-</em>
                                          </MenuItem>
                                          {CALIFICARE_OPTIONS.map((option, index) => (
                                            <MenuItem key={index} value={option.value}>
                                              {option.label}
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                    </Box>
                                    </>
                                  )}
                                </Box>
                                </>
                              ) : '-'}
                            </TableCell>
                            {/* Next stage (editable) */}
                            <TableCell sx={{ padding: '8px 4px', width: '33.33%' }}>
                              {nextType ? (
                                <>
                                  <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'flex', alignItems: 'center' }}>{STAGE_ICONS[nextType]}{STAGE_LABELS[nextType]}</Typography>
                                <Box display="flex" flexDirection="row" gap={1}>
                                  <Box sx={{ flex: 2, minWidth: 120, display: 'flex', flexDirection: 'column' }}>
                                  <Typography variant="caption" sx={{ ml: 0.5, mb: 0.5 }}>Data</Typography>
                                  <DatePicker
                                    formatDensity='dense'
                                    value={getDateValue(client.id, getColumnIndex(nextType))}
                                    onChange={(date) => handleDateChange(client.id, getColumnIndex(nextType), date)}
                                    slotProps={{
                                      textField: {
                                        size: 'small',
                                        fullWidth: true,
                                        sx: {
                                          minWidth: 90,
                                          maxWidth: 140,
                                          p: 0,
                                          background: '#f7f7fa',
                                          borderRadius: 2,
                                          fontSize: '0.95rem',
                                          '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            background: '#f7f7fa',
                                            fontSize: '0.95rem',
                                            height: 36,
                                          },
                                          '& input': {
                                            fontSize: '0.95rem',
                                            padding: '6px 10px',
                                          },
                                        },
                                        InputProps: {
                                          sx: {
                                            borderRadius: 2,
                                            background: '#f7f7fa',
                                            fontSize: '0.95rem',
                                          },
                                        },
                                        onClick: (e: React.MouseEvent<HTMLInputElement>) => {
                                          const button = e.currentTarget.parentElement?.querySelector('button');
                                          if (button) (button as HTMLButtonElement).click();
                                        },
                                      }
                                    }}
                                    format="dd/MM/yyyy"
                                  />
                                </Box>
                                  {/* Contractare special UI */}
                                  {nextType === ClientStageType.CONTRACTARE ? (
                                    <>
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography variant="caption" sx={{ ml: 0.5 }}>Semnare</Typography>
                                      <FormControl size="small" fullWidth sx={{ background: '#f7f7fa', borderRadius: 2 }}>
                                        <Select
                                          value={getSemnareValue(client.id)}
                                          onChange={(e) => handleSemnareChange(client.id, e.target.value)}
                                          displayEmpty
                                          sx={{
                                            fontSize: '0.95rem',
                                            borderRadius: 2,
                                            background: '#f7f7fa',
                                            height: 36,
                                            minHeight: 36,
                                            '.MuiSelect-select': {
                                              padding: '6px 10px',
                                            },
                                          }}
                                          MenuProps={{
                                            PaperProps: {
                                              sx: { borderRadius: 2 }
                                            }
                                          }}
                                        >
                                          <MenuItem value=""><em>-</em></MenuItem>
                                          {DA_NU_OPTIONS.map(opt => (
                                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography variant="caption" sx={{ ml: 0.5 }}>Incasare</Typography>
                                      <FormControl size="small" fullWidth sx={{ background: '#f7f7fa', borderRadius: 2 }}>
                                        <Select
                                          value={getIncasareValue(client.id)}
                                          onChange={(e) => handleIncasareChange(client.id, e.target.value)}
                                          displayEmpty
                                          sx={{
                                            fontSize: '0.95rem',
                                            borderRadius: 2,
                                            background: '#f7f7fa',
                                            height: 36,
                                            minHeight: 36,
                                            '.MuiSelect-select': {
                                              padding: '6px 10px',
                                            },
                                          }}
                                          MenuProps={{
                                            PaperProps: {
                                              sx: { borderRadius: 2 }
                                            }
                                          }}
                                        >
                                          <MenuItem value=""><em>-</em></MenuItem>
                                          {DA_NU_OPTIONS.map(opt => (
                                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                    </Box>
                                    </>
                                  ) : (
                                    <>
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography variant="caption" sx={{ ml: 0.5 }}>STELUTA</Typography>
                                      <FormControl size="small" fullWidth sx={{ background: '#f7f7fa', borderRadius: 2 }}>
                                        <Select
                                          IconComponent={() => null}
                                          value={getStelutaValue(client.id, getColumnIndex(nextType))}
                                          onChange={(e) => handleStelutaChange(client.id, getColumnIndex(nextType), e.target.value)}
                                          displayEmpty
                                          sx={{
                                            fontSize: '0.95rem',
                                            borderRadius: 2,
                                            background: '#f7f7fa',
                                            height: 36,
                                            minHeight: 36,
                                            '.MuiSelect-select': {
                                              padding: '6px 10px',
                                            },
                                          }}
                                          MenuProps={{
                                            PaperProps: {
                                              sx: { borderRadius: 2 }
                                            }
                                          }}
                                        >
                                          <MenuItem value="">
                                            <em>-</em>
                                          </MenuItem>
                                          {STELUTA_OPTIONS.map((option, index) => (
                                            <MenuItem key={index} value={option.value}>
                                              <Tooltip title={option.tooltip} placement="top">
                                                {option.label}
                                              </Tooltip>
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography variant="caption" sx={{ ml: 0.5 }}>Calificare</Typography>
                                      <FormControl size="small" fullWidth sx={{ background: '#f7f7fa', borderRadius: 2 }}>
                                        <Select
                                          IconComponent={() => null}
                                          value={getCalificareValue(client.id, getColumnIndex(nextType))}
                                          onChange={(e) => handleCalificareChange(client.id, getColumnIndex(nextType), e.target.value)}
                                          displayEmpty
                                          sx={{
                                            fontSize: '0.95rem',
                                            borderRadius: 2,
                                            background: '#f7f7fa',
                                            height: 36,
                                            minHeight: 36,
                                            '.MuiSelect-select': {
                                              padding: '6px 10px',
                                            },
                                          }}
                                          MenuProps={{
                                            PaperProps: {
                                              sx: { borderRadius: 2 }
                                            }
                                          }}
                                        >
                                          <MenuItem value="">
                                            <em>-</em>
                                          </MenuItem>
                                          {CALIFICARE_OPTIONS.map((option, index) => (
                                            <MenuItem key={index} value={option.value}>
                                              {option.label}
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                    </Box>
                                    </>
                                  )}
                                </Box>
                                </>
                              ) : '-'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                                 <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} px={2}>
                   <Typography variant="body2" color="text.secondary">
                     Afișare {((page - 1) * limit) + 1}-{Math.min(page * limit, filteredClients.length)} din {filteredClients.length} clienți
                   </Typography>
                  <Box display="flex" alignItems="center" gap={2}>
                    <FormControl size="small" sx={{ minWidth: 80 }}>
                      <Select
                        value={limit}
                        onChange={(e) => {
                          setLimit(Number(e.target.value));
                          setPage(1); // Reset to first page when changing limit
                        }}
                        sx={{
                          fontSize: '0.875rem',
                          height: 32,
                          '.MuiSelect-select': {
                            padding: '4px 8px',
                          },
                        }}
                      >
                        <MenuItem value={5}>5</MenuItem>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={20}>20</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                      </Select>
                    </FormControl>
                                         <Pagination
                       count={Math.ceil(filteredClients.length / limit)}
                       page={page}
                       onChange={(_, newPage) => setPage(newPage)}
                       size="small"
                       color="primary"
                       showFirstButton
                       showLastButton
                     />
                  </Box>
                </Box>
                </>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Hyperlink Modal */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>Adaugă Client din Hyperlink</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Copiați hyperlink-ul și apăsați butonul pentru a-l citi din clipboard.
          </Typography>
          <Box display="flex" gap={2} mb={3}>
            <Button
              variant="outlined"
              onClick={readFromClipboard}
              sx={{ flexShrink: 0 }}
            >
              Citește din Clipboard
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
              sau lipiți manual mai jos
            </Typography>
          </Box>
          {/* Show extracted data above manual entry */}
          {extractedData && (
            <Box mt={3} p={2} bgcolor="grey.50" borderRadius={1} border={1} borderColor="grey.300">
              <Typography variant="subtitle2" fontWeight={600} mb={1}>
                Date extrase:
              </Typography>
              <Box>
                <Typography variant="body2">
                  <strong>Nume Client:</strong> {extractedData.customerName}
                </Typography>
                <Typography variant="body2">
                  <strong>URL:</strong> {extractedData.url}
                </Typography>
                <Typography variant="body2">
                  <strong>ID Lucrare:</strong> {extractedData.idLucrare}
                </Typography>
              </Box>
            </Box>
          )}
          {/* Manual entry section */}
          <Box mb={2}>
            <TextField
              label="Nume Client"
              value={manualName}
              onChange={e => setManualName(e.target.value)}
              fullWidth
              margin="dense"
              size="small"
            />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5, mb: 0.5, display: 'block' }}>
              (trebuie să conțină id_lucrare)
            </Typography>
            <TextField
              label="URL"
              value={manualUrl}
              onChange={e => setManualUrl(e.target.value)}
              fullWidth
              margin="dense"
              size="small"
              sx={{ mt: 1 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5, mb: 0.5, display: 'block' }}>
              (trebuie să conțină id_lucrare)
            </Typography>
            {manualError && (
              <Alert severity="error" sx={{ mt: 1 }}>{manualError}</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Anulează</Button>
          <Button 
            onClick={handleSubmitHyperlink} 
            variant="contained"
            sx={{ backgroundColor: '#BA1010', '&:hover': { backgroundColor: '#8B0A0A' } }}
          >
            Procesează
          </Button>
        </DialogActions>
      </Dialog>

      {/* Comment Modal */}
      <Dialog open={commentModalOpen} onClose={() => setCommentModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Comentarii Client</DialogTitle>
        <DialogContent>
          {commentLoading ? (
            <Box display="flex" justifyContent="center" p={2}><CircularProgress /></Box>
          ) : (
            <>
              {comments.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Nu există comentarii pentru acest client.</Typography>
              ) : (
                <Box mb={2}>
                  {comments.map((c, idx) => (
                    <Box key={c.comment_id || idx} mb={1} p={1} bgcolor="#f5f5f5" borderRadius={1} display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{c.user_username || c.userId}</Typography>
                        <Typography variant="body2">{c.comment_comment}</Typography>
                        <Typography variant="caption" color="text.secondary">{c.comment_createdAt ? new Date(c.comment_createdAt).toLocaleString() : ''}</Typography>
                      </Box>
                      {currentUserId && c.user_id === currentUserId && (
                        <IconButton size="small" color="error" onClick={() => handleDeleteComment(c.comment_id)} title="Șterge comentariul">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
              {commentError && <Alert severity="error">{commentError}</Alert>}
              <TextField
                label="Adaugă comentariu"
                value={newComment}
                onChange={e => setNewComment(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                maxRows={4}
                sx={{ mt: 2 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommentModalOpen(false)}>Închide</Button>
          <Button onClick={handleAddComment} variant="contained" disabled={commentLoading || !newComment.trim()}>
            Adaugă
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default ClientPipelinePage; 